import { useEffect, useState } from "react"
import { useParams, Link } from "react-router-dom"
import { ROUTES } from '@/constants/routes'
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../../components/ui/card"
import { Button } from "../../components/ui/button"
import {
  BookOpen,
  Play,
  CheckCircle,
  Clock,
  Loader2,
  ArrowLeft,
} from "lucide-react"
import { getCourse, type CourseDetail, type LessonItem } from "../../api/aiTeam"

export default function CourseDetailPage() {
  const { courseId } = useParams<{ courseId: string }>()
  const [course, setCourse] = useState<CourseDetail | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (courseId) {
      loadCourse(courseId)
    }
  }, [courseId])

  async function loadCourse(id: string) {
    try {
      const data = await getCourse(id)
      setCourse(data)
    } catch (err) {
      console.error("Failed to load course:", err)
    } finally {
      setLoading(false)
    }
  }

  const statusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-5 h-5 text-green-500" />
      case "in_progress":
        return <Play className="w-5 h-5 text-blue-500" />
      default:
        return <Clock className="w-5 h-5 text-slate-300" />
    }
  }

  const statusLabel = (status: string) => {
    switch (status) {
      case "completed":
        return "已完成"
      case "in_progress":
        return "学习中"
      default:
        return "未开始"
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    )
  }

  if (!course) {
    return (
      <div className="container mx-auto py-8 px-4">
        <div className="text-center py-12">
          <p className="text-slate-500">课程不存在</p>
          <Link to={ROUTES.AI_TEAM_COURSES}>
            <Button variant="ghost" className="mt-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              返回课程列表
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  const progress =
    course.total_lessons > 0
      ? Math.round((course.completed_lessons / course.total_lessons) * 100)
      : 0

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="mb-6">
        <Link to={ROUTES.AI_TEAM_COURSES}>
          <Button variant="ghost" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            返回课程列表
          </Button>
        </Link>
      </div>

      {/* Course Header */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl">{course.title}</CardTitle>
              <p className="text-slate-500 mt-1">
                {course.course_type} · {course.subject}
              </p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-blue-600">{progress}%</div>
              <p className="text-sm text-slate-500">完成进度</p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="mt-4 flex items-center gap-4 text-sm text-slate-500">
            <span className="flex items-center gap-1">
              <BookOpen className="w-4 h-4" />
              {course.total_lessons} 节课
            </span>
            <span className="flex items-center gap-1">
              <CheckCircle className="w-4 h-4" />
              {course.completed_lessons} 节已完成
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Lessons List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">课程目录</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {course.lessons?.map((lesson: LessonItem, idx: number) => (
              <div
                key={lesson.id}
                className="flex items-center gap-4 p-4 rounded-lg border border-slate-200 hover:border-blue-200 hover:bg-blue-50/50 transition-colors"
              >
                <div className="flex-shrink-0">{statusIcon(lesson.status)}</div>

                <div className="flex-grow">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-400">
                      第{idx + 1}节
                    </span>
                    <span className="font-medium">{lesson.title}</span>
                  </div>

                  {lesson.objectives?.length > 0 && (
                    <p className="text-sm text-slate-500 mt-1">
                      {lesson.objectives[0]}
                    </p>
                  )}

                  <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {lesson.duration}分钟
                    </span>
                    {lesson.score !== undefined && (
                      <span className="text-green-600 font-medium">
                        得分: {lesson.score}
                      </span>
                    )}
                  </div>
                </div>

                <Link to={`/ai-team/lessons/${lesson.id}`}>
                  <Button
                    variant={lesson.status === "completed" ? "outline" : "default"}
                    size="sm"
                  >
                    {lesson.status === "completed"
                      ? "复习"
                      : lesson.status === "in_progress"
                      ? "继续"
                      : "开始"}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
